import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { FileDown, Printer, Search, Upload, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fileUrl = (f, download = true) =>
  `${BACKEND}${f.url_path}?${download ? "download=1&" : ""}v=${encodeURIComponent(f.updated_at || "")}`;

export const AdminLabels = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const pendingReplace = useRef(null); // storage path being replaced, or null for new upload

  const load = () => {
    api.get("/admin/labels")
      .then(({ data }) => setFiles(data))
      .catch(() => toast.error("Failed to load labels"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pickFile = (replacePath = null) => {
    pendingReplace.current = replacePath;
    inputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Labels must be PDF files");
      return;
    }
    const replacePath = pendingReplace.current;
    const form = new FormData();
    form.append("file", file);
    if (replacePath) form.append("replace_path", replacePath);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/labels/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.replaced ? `${data.name} replaced` : `${data.name} uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      pendingReplace.current = null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allLabels = files.find((f) => f.name === "ALL_LABELS.pdf");
  const allSingles = files.filter((f) => f.name !== "ALL_LABELS.pdf");
  const q = search.trim().toLowerCase();
  const singles = q ? allSingles.filter((f) => f.name.toLowerCase().includes(q)) : allSingles;
  const storagePath = (f) => f.url_path.replace("/api/files/", "");

  return (
    <div className="max-w-3xl" data-testid="admin-labels-section">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={onFileChosen}
        className="hidden"
        data-testid="label-file-input"
      />

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8 mb-8">
        <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Print-Ready Vial Labels</h2>
        <p className="text-sm text-[#6E675F] mb-6">
          Each PDF is sized at exactly <strong>40mm × 20mm</strong> — send them straight to your label printer or print shop.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {allLabels && (
            <a
              href={fileUrl(allLabels)}
              download="ALL_LABELS.pdf"
              data-testid="download-all-labels-button"
              className="inline-flex items-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#75665A] transition-colors"
            >
              <Printer className="w-4 h-4" /> Download All Labels (one PDF, {allSingles.length} pages)
            </a>
          )}
          <button
            data-testid="upload-label-button"
            onClick={() => pickFile(null)}
            disabled={uploading}
            className="inline-flex items-center gap-2 border border-[#8C7B6B] text-[#8C7B6B] text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#8C7B6B] hover:text-white transition-colors disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Label PDF"}
          </button>
        </div>
        <p className="text-xs text-[#6E675F] mt-4">
          Uploading a PDF with the same filename overwrites that label. Use a row's <strong>Replace</strong> button to swap a
          label's file while keeping its name.
        </p>
        {allLabels && (
          <button
            data-testid="replace-all-labels-button"
            onClick={() => pickFile(storagePath(allLabels))}
            disabled={uploading}
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#8C7B6B] font-semibold uppercase tracking-[0.1em] hover:text-[#1A1A1A] transition-colors disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Replace ALL_LABELS.pdf
          </button>
        )}
      </div>

      {allSingles.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 text-[#8C7B6B] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="admin-label-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search labels…"
              className="pl-9"
            />
          </div>
          <p className="text-sm text-[#6E675F]" data-testid="admin-labels-count">
            {singles.length} label{singles.length === 1 ? "" : "s"}
          </p>
        </div>
      )}

      {allSingles.length === 0 && (
        <p className="text-sm text-[#6E675F]" data-testid="admin-no-labels">No labels yet — upload your first PDF above.</p>
      )}
      {allSingles.length > 0 && singles.length === 0 && (
        <p className="text-sm text-[#6E675F] py-6 text-center" data-testid="admin-no-labels-match">No labels match "{search.trim()}".</p>
      )}
      {singles.length > 0 && (
        <div className="border border-[#DDD7CF] rounded-lg bg-white divide-y divide-[#EFEBE6]" data-testid="admin-labels-list">
          {singles.map((f) => (
            <div
              key={f.name}
              className="flex items-center justify-between gap-3 px-6 py-3.5 hover:bg-[#F7F5F2] transition-colors"
              data-testid={`label-row-${f.name.replace(".pdf", "")}`}
            >
              <span className="text-sm text-[#1A1A1A] font-medium truncate">{f.name}</span>
              <span className="flex items-center gap-4 shrink-0">
                <button
                  data-testid={`label-replace-${f.name.replace(".pdf", "")}`}
                  onClick={() => pickFile(storagePath(f))}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-xs text-[#6E675F] font-semibold uppercase tracking-[0.1em] hover:text-[#1A1A1A] transition-colors disabled:opacity-50"
                  title={`Replace ${f.name} with a new PDF`}
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Replace
                </button>
                <a
                  href={fileUrl(f)}
                  download={f.name}
                  data-testid={`label-download-${f.name.replace(".pdf", "")}`}
                  className="inline-flex items-center gap-2 text-xs text-[#8C7B6B] font-semibold uppercase tracking-[0.1em] hover:text-[#1A1A1A] transition-colors"
                >
                  <FileDown className="w-4 h-4" /> PDF
                </a>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
